import './Dialog.scss'
import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type Ref,
  type RefObject
} from 'react'
import { MdOutlineClose } from 'react-icons/md'
import { clx } from '../clx'
import { IconButton } from '../IconButton'
import { Portal, useModalLayer, usePresence } from '../layer'

export type DialogCloseSource = 'escape' | 'scrim' | 'close-button'

export interface DialogProps {
  isOpen: boolean
  /** Asked to close. The parent decides; the dialog never closes itself. */
  onClose: (source: DialogCloseSource) => void
  /** Accessible name (`aria-labelledby`). */
  title: ReactNode
  /** Optional lead text, linked with `aria-describedby`. */
  description?: ReactNode
  /** The body. It scrolls inside the dialog when tall. */
  children?: ReactNode
  /** Actions, pinned below the body. */
  footer?: ReactNode
  /** Maximum inline size; always capped at 100% of the viewport. */
  size?: 'sm' | 'md' | 'lg'
  /** `alertdialog` for a decision that interrupts (destructive confirmations). */
  role?: 'dialog' | 'alertdialog'
  /** Default true. False: no close button, Escape and the scrim do nothing. */
  isDismissable?: boolean
  /** Element focused on open. Defaults to the first tabbable, else the dialog. */
  initialFocus?: RefObject<HTMLElement | null>
  /** The dialog surface (the element with the role). */
  ref?: Ref<HTMLElement>
  className?: string
}

/**
 * Modal surface: portalled, focus-trapped, background inert, page scroll
 * locked, Escape through the shared stack, focus returned on close. Full
 * screen below `phablet`.
 */
export function Dialog({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  role = 'dialog',
  isDismissable = true,
  initialFocus,
  ref,
  className
}: DialogProps) {
  const id = useId()
  const titleId = `dialog-title-${id}`
  const descriptionId = `dialog-description-${id}`
  const layerRef = useRef<HTMLDivElement>(null)
  const pressStartedOnScrim = useRef(false)

  // Keep the Escape handler identity stable: a parent re-rendering with a new
  // inline onClose must not re-push this layer above one opened on top of it.
  const onCloseRef = useRef(onClose)
  useLayoutEffect(() => {
    onCloseRef.current = onClose
  })
  const onEscape = useCallback(() => {
    onCloseRef.current('escape')
  }, [])

  useModalLayer(layerRef, {
    open: isOpen,
    onEscape: isDismissable ? onEscape : undefined,
    initialFocus
  })

  // Stays mounted briefly after closing to animate out (ADR 0019); the
  // modal layer above already released focus, inert and Escape.
  const { isMounted, isClosing } = usePresence(isOpen)
  if (!isMounted) return null

  return (
    <Portal>
      <div
        ref={layerRef}
        className={clx('dialog', `dialog--is-${size}`, className)}
        data-state={isClosing ? 'closed' : 'open'}
        inert={isClosing || undefined}
        onMouseDown={(event) => {
          pressStartedOnScrim.current = event.target === event.currentTarget
        }}
        onClick={(event) => {
          const onScrim = pressStartedOnScrim.current && event.target === event.currentTarget
          pressStartedOnScrim.current = false
          if (onScrim && isDismissable) onClose('scrim')
        }}
      >
        <section
          ref={ref}
          role={role}
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
          tabIndex={-1}
          className="dialog__surface"
        >
          <header className="dialog__header">
            <h2 id={titleId} className="dialog__title">
              {title}
            </h2>
            {isDismissable && (
              <IconButton
                label="Close"
                icon={<MdOutlineClose />}
                hasTooltip={false}
                className="dialog__close"
                onClick={() => {
                  onClose('close-button')
                }}
              />
            )}
          </header>
          <div className="dialog__body">
            {description && (
              <div id={descriptionId} className="dialog__description">
                {description}
              </div>
            )}
            {children}
          </div>
          {footer && <footer className="dialog__footer">{footer}</footer>}
        </section>
      </div>
    </Portal>
  )
}
